<template>
  <el-dialog
    append-to-body
    :title="task.canExecute ? '完成每日任务' : '每日任务详情'"
    :visible="visible"
    width="900px"
    :close-on-click-modal="false"
    @close="$emit('update:visible', false)"
  >
    <div
      v-loading="loading"
      class="detail-container"
    >
      <template v-if="task.uuid">
        <DailyTaskInfo :task="task" />
        <section class="detail-section">
          <el-button
            v-if="task.faultId"
            class="fault-record-banner"
            @click="faultVisible = true"
          >
            已记录故障单，单号：{{ task.faultCode || '-' }}
            <i class="el-icon-arrow-right" />
          </el-button>
          <template v-if="parseImageFileIds(task.taskImageFileIds).length">
            <div class="section-title mt-4">
              任务图片
            </div>
            <ImageThumbViewer
              :file-ids="task.taskImageFileIds"
              variant="compact"
              preview-width="640px"
            />
          </template>
          <template v-if="feedbackImageIds.length">
            <div class="section-title mt-4">
              现场及异常图片
            </div>
            <ImageThumbViewer
              :file-ids="feedbackImageIds"
              variant="compact"
              preview-width="640px"
            />
          </template>
        </section>
        <el-alert
          v-if="task.status === 'pending' && !task.canExecute"
          title="请使用本部门的数据管理员、任务管理员或任务执行员账号填写完成信息。"
          type="info"
          :closable="false"
          show-icon
        />
        <el-form
          v-if="task.canExecute"
          ref="executeForm"
          :model="fault"
          :rules="executeRules"
          label-width="110px"
          :disabled="saving || loading"
          class="execute-form"
        >
          <section class="detail-section">
            <div class="section-title">
              填写完成信息
            </div>
            <el-form-item label="是否异常">
              <el-radio-group v-model="result.hasFault">
                <el-radio :label="0">
                  正常
                </el-radio>
                <el-radio :label="1">
                  有异常
                </el-radio>
              </el-radio-group>
            </el-form-item>
            <el-form-item label="完成情况">
              <el-input
                v-model="result.workResult"
                type="textarea"
                maxlength="2000"
                :rows="3"
                show-word-limit
                placeholder="填写完成情况（选填）"
              />
            </el-form-item>
            <el-form-item label="现场图片">
              <ImageUploader
                ref="photos"
                v-model="result.imageFileIds"
                :max="5"
                :show-zoom="true"
                tip="最多上传5张"
              />
            </el-form-item>
          </section>
          <template v-if="result.hasFault === 1">
            <section class="detail-section">
              <div class="section-title">
                异常信息
              </div>
              <el-alert
                class="fault-alert"
                title="提交完成后自动创建故障单，后续在故障处理中维修。"
                :closable="false"
                type="warning"
              />
              <el-form-item
                label="异常对象名称"
                prop="deviceName"
              >
                <el-input
                  v-model="fault.deviceName"
                  maxlength="100"
                  placeholder="填写异常对象名称"
                />
              </el-form-item>
              <el-form-item label="故障位置">
                <el-input
                  v-model="fault.installLocation"
                  maxlength="500"
                />
              </el-form-item>
              <el-form-item
                label="异常描述"
                prop="faultDesc"
              >
                <el-input
                  v-model="fault.faultDesc"
                  type="textarea"
                  maxlength="2000"
                  :rows="3"
                  show-word-limit
                  placeholder="填写异常情况"
                />
              </el-form-item>
              <el-form-item label="异常图片">
                <ImageUploader
                  ref="faultPhotos"
                  v-model="fault.faultImages"
                  :max="9"
                  :show-zoom="true"
                  tip="最多上传9张"
                />
              </el-form-item>
            </section>
            <section class="detail-section">
              <div class="section-title">
                分配信息
              </div>
              <AssignTypeFields
                :model="fault"
                team-type="fault"
                preset="fault"
                label-width="110px"
                user-form-prop="assignUserIds"
                team-form-prop="assignTeamId"
              />
            </section>
          </template>
        </el-form>
      </template>
    </div>
    <div slot="footer">
      <el-button @click="$emit('update:visible', false)">
        关闭
      </el-button>
      <el-button
        v-if="task.canExecute"
        type="primary"
        :loading="saving"
        :disabled="loading"
        @click="complete"
      >
        完成任务
      </el-button>
    </div>
    <FaultDetailDialog
      v-if="faultVisible"
      :visible.sync="faultVisible"
      :fault-id="task.faultId"
    />
  </el-dialog>
</template>
<script>
import { dailyTaskService, unwrap } from './daily-task.service';
import ImageUploader from '@/features/components/common-image/ImageUploader.vue';
import ImageThumbViewer from '@/features/components/common-image/ImageThumbViewer.vue';
import AssignTypeFields from '@/features/gnmk/components/AssignTypeFields.vue';
import FaultDetailDialog from '@/features/gnmk/fault/faultDetailDialog.vue';
import DailyTaskInfo from './DailyTaskInfo.vue';
import { parseImageFileIds, uniqueFileIds } from '@/features/components/common-image/imageFileIdsUtil.js';
export default {
  name: 'DailyTaskExecuteDialog',
  components: { DailyTaskInfo, ImageUploader, ImageThumbViewer, AssignTypeFields, FaultDetailDialog },
  props: { visible: Boolean, taskId: { type: String, required: true } },
  data () {
    return {
      task: {},
      loading: false,
      saving: false,
      faultVisible: false,
      result: { workResult: '', imageFileIds: [], hasFault: 0 },
      fault: {
        deviceName: '',
        installLocation: '',
        faultDesc: '',
        faultImages: [],
        assignType: 'none',
        assignUserIds: [],
        assignTeamId: null
      },
      executeRules: {
        deviceName: [{ required: true, whitespace: true, message: '请填写异常对象名称', trigger: 'blur' }],
        faultDesc: [{ required: true, whitespace: true, message: '请填写异常描述', trigger: 'blur' }],
        assignUserIds: [{ required: true, type: 'array', message: '请选择维修人员', trigger: 'change' }],
        assignTeamId: [{ required: true, message: '请选择维修小组', trigger: 'change' }]
      }
    };
  },
  computed: {
    feedbackImageIds () {
      return uniqueFileIds([
        ...parseImageFileIds(this.task.imageFileIds),
        ...parseImageFileIds(this.task.faultImageFileIds)
      ]);
    }
  },
  methods: {
    parseImageFileIds,
    async load () {
      this.loading = true;
      try {
        this.task = unwrap(await dailyTaskService.detail(this.taskId));
        this.fault.installLocation = this.task.workLocation;
      } catch (e) {
        this.$message.error('任务详情加载失败');
      } finally {
        this.loading = false;
      }
    },
    async complete () {
      if (!this.task.canExecute || this.saving || this.loading) {
        return;
      }
      if ([this.$refs.photos, this.$refs.faultPhotos].some(r => r && r.isUploading())) {
        return this.$message.warning('请等待图片上传完成');
      }
      if (this.result.imageFileIds.length > 5) {
        return this.$message.warning('现场图片最多5张');
      }
      if (this.result.hasFault === 1) {
        if (this.fault.faultImages.length > 9) {
          return this.$message.warning('异常图片最多9张');
        }
        try {
          await this.$refs.executeForm.validate();
        } catch (e) {
          return;
        }
      }
      this.saving = true;
      try {
        this.task = unwrap(
          await dailyTaskService.complete({
            uuid: this.task.uuid,
            version: this.task.version,
            ...this.result,
            fault:
              this.result.hasFault === 1
                ? {
                  ...this.fault,
                  deviceSource: 'external',
                  areaId: this.task.areaId || this.task.zoneAreaId,
                  assignUserIds: this.fault.assignType === 'user' ? this.fault.assignUserIds.join(',') : null,
                  assignTeamId: this.fault.assignType === 'team' ? this.fault.assignTeamId : null
                }
                : null
          })
        );
        this.$message.success('任务已完成，工作日志将自动生成');
        this.$emit('success');
      } catch (e) {
        this.$message.error('完成失败，请检查提示或刷新任务后重试');
      } finally {
        this.saving = false;
      }
    }
  },
  created () {
    this.load();
  }
};
</script>
<style scoped>
.detail-container {
  min-height: 120px;
  padding: 0 4px;
}
.detail-section {
  margin-bottom: 16px;
}
.detail-section:last-child { margin-bottom: 0; }
.detail-section > .el-form-item:last-child { margin-bottom: 0; }
.fault-record-banner {
  display: block;
  width: 100%;
  margin-top: 16px;
  padding: 12px 16px;
  color: #d46b08;
  background: #fff7e6;
  border-color: #ffe7ba;
  text-align: left;
  white-space: normal;
  overflow-wrap: anywhere;
}
.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 14px;
  padding-left: 10px;
  border-left: 4px solid #409eff;
  line-height: 1.1;
}
.mt-4 { margin-top: 20px; }
.fault-tag { margin-left: 8px; }
.fault-alert { margin-bottom: 18px; }
.execute-form {
  margin-top: 20px;
}
.el-select {
  width: 100%;
}
</style>
